export const appShellClasses = "min-h-screen flex flex-col";

export const appMainClasses = "flex-grow";

export const alertBaseClasses = "rounded-lg border px-3 py-2 text-sm";

export const alertToneClasses = {
  error: "border-red-200 bg-red-50 text-red-700",
  success: "border-green-200 bg-green-50 text-green-700",
} as const;

export const emptyStateClasses = {
  wrapper: "rounded-2xl border border-dashed border-gray-200 bg-white/60 p-10 text-center",
  iconWrap: "mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-blue-50",
  icon: "h-6 w-6 text-blue-600",
  title: "mt-4 text-lg font-semibold text-gray-900",
  subtitle: "mt-1 text-gray-600",
  action: "mt-6",
} as const;

export const footerClasses = {
  wrapper: "border-t border-[#cfb999] bg-[#244454] text-[#f7efe4]",
  content: "mx-auto max-w-7xl px-6 py-10",
  brandBlock: "text-center",
  eyebrow: "mb-3 inline-flex rounded-full border border-[#4c6a78] bg-[#2d5364] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-[#f0c48c]",
  brandTitle: "mb-1 text-lg font-semibold text-white",
  brandCopy: "text-sm text-[#d7c4af]",
  detailCopy: "mt-3 text-sm leading-6 text-[#e6d5c0]",
  communityLink: "mt-4 inline-flex text-sm font-medium text-[#f0c48c] transition hover:text-white",
  nav: "flex justify-center gap-6 text-sm text-[#f1dfc9]",
  navLink: "transition hover:text-white",
  socialList: "flex justify-center gap-3 md:justify-end",
  socialLink: "rounded-full border border-[#4c6a78] p-2 text-[#f1dfc9] transition hover:border-[#f0c48c] hover:text-white",
  socialIcon: "h-5 w-5",
  legal: "border-t border-[#355766] py-4 text-center text-xs text-[#d7c4af]",
  photoCreditLink: "underline transition hover:text-white",
} as const;

export const filterBarClasses = {
  wrapper: "p-3 sm:p-6",
  layout: "flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between",
  searchWrap: "relative w-full",
  searchIcon: "pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-[#8d877e]",
  searchInput:
    "w-full rounded-2xl border border-[#ddd1bf] bg-white py-3 pl-10 pr-3 text-[15px] text-[#213547] shadow-[0_10px_30px_rgba(84,62,39,0.06)] focus:outline-none focus:ring-4 focus:ring-[#f5d9bf] sm:py-2.5",
  controls: "flex items-center gap-2 sm:shrink-0",
  label: "text-sm font-medium text-[#5c5b57]",
  select:
    "rounded-2xl border border-[#ddd1bf] bg-white px-3 py-3 text-sm text-[#213547] shadow-[0_10px_30px_rgba(84,62,39,0.06)] focus:outline-none focus:ring-4 focus:ring-[#f5d9bf] sm:py-2.5",
} as const;

export const jobCardClasses = {
  savedBadge:
    "inline-flex h-5 w-5 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 ring-1 ring-inset ring-emerald-200",
  savedBadgeIcon: "h-3.5 w-3.5",
  savedBadgeSrOnly: "sr-only",
  wrapper:
    "group relative flex h-full overflow-hidden transition hover:-translate-y-1 hover:shadow-[0_28px_70px_rgba(84,62,39,0.14)]",
  inner: "relative flex w-full flex-1 flex-col p-4 sm:p-5",
  hoverGlow:
    "pointer-events-none absolute inset-0 z-0 bg-[linear-gradient(135deg,rgba(226,239,235,0.55),rgba(255,255,255,0.1),rgba(239,201,165,0.3))] opacity-0 transition group-hover:opacity-100",
  content: "relative z-10 flex h-full flex-col",
  header: "mb-1 flex items-start gap-3",
  titleWrap: "min-w-0 flex-1",
  title: "truncate text-base font-semibold text-[#213547] sm:text-lg",
  badgeRow: "flex flex-shrink-0 items-center gap-2",
  categoryBadge:
    "inline-flex items-center whitespace-nowrap rounded-full bg-[#f7e7d1] px-2.5 py-1 text-[11px] font-medium text-[#a2572b] ring-1 ring-inset ring-[#e2c29b] sm:text-xs",
  company: "mb-2 text-sm text-[#7b766e]",
  metaList: "mb-3 space-y-1 text-[13px] text-[#3d5867] sm:text-sm",
  metaRow: "flex items-center gap-2",
  currencyRow: "flex items-center gap-2 font-semibold text-emerald-700",
  metaIcon: "h-4 w-4",
  descriptionWrap: "flex-1",
  description: "line-clamp-2 text-sm text-[#5c5b57]",
  footer: "mt-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between",
  date: "order-2 text-xs italic text-[#7b766e] sm:order-1",
  actions: "order-1 flex flex-col gap-2 sm:order-2 sm:flex-row",
  primaryLink: "w-full px-4 py-3 sm:w-auto sm:py-2.5",
  primaryLinkIcon: "h-4 w-4",
} as const;

export const navbarClasses = {
  wrapper:
    "sticky top-0 z-50 border-b border-[#eadcc9] bg-[#f7f0e5]/92 backdrop-blur-xl supports-[backdrop-filter]:bg-[#f7f0e5]/82",
  container: "mx-auto max-w-7xl px-4 sm:px-6",
  content: "flex min-h-16 items-center justify-between gap-4 py-2",
  publicSearchWrap: "relative hidden flex-1 md:block",
  publicSearchMobileWrap: "relative pb-3 md:hidden",
  publicFilterIcon: "pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#8d877e]",
  publicFilterInput:
    "w-full rounded-full border border-[#d8cab4] bg-white/95 py-2.5 pl-9 pr-4 text-sm text-[#213547] shadow-[0_10px_24px_rgba(84,62,39,0.06)] outline-none transition focus:border-[#c96d3b] focus:ring-4 focus:ring-[#f5d9bf]",
  brandButton: "flex flex-col items-start justify-center text-left transition hover:opacity-90",
  brandTitle: "text-lg font-bold tracking-tight text-[#213547] sm:text-xl",
  brandSubtitle: "hidden text-[11px] font-medium uppercase tracking-[0.28em] text-[#a2572b] sm:block",
  desktopNav:
    "hidden items-center gap-1 rounded-[22px] border border-[#eadcc9] bg-[#fffaf3] p-1 shadow-[inset_0_1px_0_rgba(255,255,255,0.85)] md:flex",
  desktopActions: "hidden items-center gap-2 md:flex",
  greeting: "hidden rounded-full bg-white px-3 py-2 text-sm text-[#5c5b57] shadow-sm lg:inline",
  mobileToggleWrap: "md:hidden",
  mobileToggle:
    "inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-[#d7c7af] bg-[#fffaf3] text-[#34566a] shadow-sm hover:bg-white focus:outline-none focus-visible:ring-4 focus-visible:ring-[#f2d6bc]",
  mobileToggleIcon: "h-6 w-6",
  mobileMenuBase: "overflow-hidden transition-[max-height,opacity] duration-300 ease-out md:hidden",
  mobileMenuOpen: "max-h-[520px] opacity-100",
  mobileMenuClosed: "max-h-0 opacity-0",
  mobileMenuPanel:
    "border-t border-[#eadcc9] bg-[#f7f0e5]/95 shadow-sm backdrop-blur-xl supports-[backdrop-filter]:bg-[#f7f0e5]/88",
  mobileMenuContent: "px-4 py-3",
  mobileGreeting: "mb-3 rounded-2xl bg-white px-3 py-2 text-sm text-[#5c5b57] shadow-sm",
  mobileStack: "flex flex-col gap-2",
  mobileDivider: "my-2 h-px bg-[#e0d2bd]",
  buttonBase:
    "inline-flex h-11 items-center justify-center rounded-2xl px-4 text-sm font-semibold transition",
  buttonGhost:
    "border border-[#d7c7af] bg-[#fff7ec] text-[#34566a] hover:-translate-y-0.5 hover:bg-white",
  buttonSoftBlue:
    "border border-[#b7d0cb] bg-[#e6f0ee] text-[#34566a] hover:-translate-y-0.5 hover:bg-[#eff6f4]",
  linkBase:
    "relative inline-flex h-11 items-center rounded-2xl px-3 text-sm font-medium transition focus:outline-none focus-visible:ring-4 focus-visible:ring-[#f2d6bc]",
  linkActive: "bg-white text-[#213547] shadow-[0_10px_25px_rgba(84,62,39,0.08)]",
  linkInactive: "text-[#5c5b57] hover:bg-white/70 hover:text-[#213547]",
} as const;

export const pageContainerClasses = {
  outer: "relative min-h-screen overflow-hidden bg-[#f5efe4]",
  innerBase: "relative z-10 mx-auto px-3 pb-12 pt-4 space-y-8 sm:px-6 sm:pb-16 sm:pt-8 sm:space-y-10",
} as const;

export const sectionTitleBaseClasses =
  "mb-4 text-xl font-semibold tracking-tight text-gray-900 sm:text-2xl";

export const skeletonGridClasses = {
  wrapper: "grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3",
  card: "animate-pulse rounded-2xl border border-gray-100 bg-white/80 p-5",
  media: "h-36 w-full rounded-lg bg-gray-200",
  lineLg: "mt-4 h-5 w-2/3 rounded bg-gray-200",
  lineSm: "mt-3 h-4 w-1/3 rounded bg-gray-200",
  lineMd: "mt-2 h-4 w-1/2 rounded bg-gray-200",
  lineShort: "mt-2 h-4 w-2/5 rounded bg-gray-200",
  button: "mt-5 h-9 w-28 rounded bg-gray-200",
} as const;

export const formComponentClasses = {
  errors: "space-y-1 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700",
  field: "",
  grid: "grid grid-cols-1 gap-4 md:grid-cols-2",
} as const;
